import { Field, Label } from '@headlessui/react'
import Photo from '../../assets/photo.svg?react'
import { useRef, useState, useEffect } from 'react'
import { ImageActionButton } from '../buttons/ImageActionButton'
import { GreyActionButton } from '../buttons/GreyActionButton'

interface IMediaFieldProps {
  label: string
  onImageSelected: (file: File) => void
  initialImageUrl?: string | null
  error?: boolean
}

export const MediaField = (props: IMediaFieldProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(props.initialImageUrl || null)

  // Update preview when initialImageUrl changes
  useEffect(() => {
    if (props.initialImageUrl) {
      setPreview(props.initialImageUrl)
    }
  }, [props.initialImageUrl])

  /**
   * Checks if an image has transparency by sampling pixel alpha values
   */
  const hasTransparency = (ctx: CanvasRenderingContext2D, width: number, height: number): boolean => {
    const imageData = ctx.getImageData(0, 0, width, height)
    const data = imageData.data
    // Check alpha channel (every 4th value starting at index 3)
    // Sample every 100th pixel for performance
    for (let i = 3; i < data.length; i += 400) {
      if (data[i] < 255) {
        return true
      }
    }
    return false
  }

  /**
   * Crops and resizes an image to a square (1024x1024 or smaller if source is smaller).
   * Crops from center based on the shorter side.
   * Preserves transparency for PNG/WebP images that have it.
   */
  const cropAndResizeImage = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Could not get canvas context'))
          return
        }

        // Determine the crop size (shorter side)
        const cropSize = Math.min(img.width, img.height)
        
        // Determine output size (max 1024, or cropSize if smaller)
        const outputSize = Math.min(cropSize, 1024)
        
        canvas.width = outputSize
        canvas.height = outputSize

        // Calculate crop position (center crop)
        const sx = (img.width - cropSize) / 2
        const sy = (img.height - cropSize) / 2

        // Draw cropped and resized image
        ctx.drawImage(
          img,
          sx, sy, cropSize, cropSize,  // source rectangle (center crop)
          0, 0, outputSize, outputSize  // destination rectangle
        )

        // Check if original format supports transparency
        const supportsTransparency = file.type === 'image/png' || file.type === 'image/webp'
        const imageHasTransparency = supportsTransparency && hasTransparency(ctx, outputSize, outputSize)

        // Use PNG if transparency is needed, otherwise JPEG for smaller file size
        const outputType = imageHasTransparency ? 'image/png' : 'image/jpeg'
        const outputExtension = imageHasTransparency ? '.png' : '.jpg'
        const quality = imageHasTransparency ? undefined : 0.9

        // Update filename extension
        const baseName = file.name.replace(/\.[^/.]+$/, '')
        const outputFileName = baseName + outputExtension

        // Convert canvas to blob, then to File
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], outputFileName, {
                type: outputType,
                lastModified: Date.now()
              })
              resolve(resizedFile)
            } else {
              reject(new Error('Could not create blob from canvas'))
            }
          },
          outputType,
          quality
        )
      }
      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = URL.createObjectURL(file)
    })
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      try {
        const resizedFile = await cropAndResizeImage(file)
        setPreview(URL.createObjectURL(resizedFile))
        props.onImageSelected(resizedFile)
      } catch (error) {
        console.error('Failed to resize image:', error)
        // Fallback to original file if resize fails
        setPreview(URL.createObjectURL(file))
        props.onImageSelected(file)
      }
    }
  }

  const onDelete = () => {
    setPreview(null)
  }

  const onChangeImage = () => {
    fileInputRef.current?.click()
  }

  const onClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <Field>
      <Label className={'text-lg'}>{props.label}</Label>

      {preview && (
        <div className={'mt-3 flex items-center gap-4'}>
          <img src={preview} className={'h-32 w-32 object-cover rounded'} />
          <div className={'flex gap-3'}>
            <GreyActionButton label={'Change image'} onClick={onChangeImage} />
            <GreyActionButton label={'Delete image'} onClick={onDelete} />
          </div>
        </div>
      )}

      {!preview && <ImageActionButton label={'Add image'} onClick={onClick} icon={<Photo width={20} height={20} />} error={props.error}/>}

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
      />
    </Field>
  )
}
